import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
// @mui
import { Box, Card, Container, Divider, Tab, Tabs } from '@mui/material';
// routes
import { PATH_DASHBOARD } from 'src/routes/paths';
// hooks
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
// components
import Page from 'src/components/Page';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
// sections
import { OrganizationManager } from 'src/careflow/ph-core/admin/organization/type/organization';
import api from 'src/ccm/services/api';
import OrganizationNewEditForm from '../components/OrganizationNewEditForm';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { ChildList } from '../components/child/ChildList';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { OrganizationUsers } from '../components/OrganizationUsers';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';

// ----------------------------------------------------------------------

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function OrganizationCreate() {
  const { themeStretch } = useSettings();

  const { i18n } = useLocales();

  const { pathname } = useLocation();

  const { uuid = '' } = useParams();

  const isEdit = pathname.includes('edit');

  const [currentOrganization, setCurrentOrganization] = useState<OrganizationManager>();
  const [currentTab, setCurrentTab] = useState(0);
  const isAllowedToChildOrgs = checkAclValidation({
    acls: [crsAcls.ADMIN.ORGANIZATIONS.CHILD_ORGANIZATIONS.VIEW],
  });

  const handleTabChange = (e: React.SyntheticEvent, selectedTab: number) => {
    setCurrentTab(selectedTab);
  };

  useEffect(() => {
    (async () => {
      const organization = await api.organizations.getOrganization(uuid);
      setCurrentOrganization(organization);
    })();
  }, []);

  return (
    <Page 
      title={!isEdit 
        ? i18n('admin.newOrganization.titlePage') 
        : i18n('admin.newOrganization.titlePageEdit')
      }
    >
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          title={
            !isEdit
              ? `${i18n('admin.newOrganization.titleCreate')}`
              : `${i18n('admin.newOrganization.titleEdit')}`
          }
          links={[
            { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
            { name: `${i18n('admin.listTitle')}`, href: PATH_DASHBOARD.organization.list },
            { name: !isEdit ? `${i18n('admin.newOrganization.title')}` : uuid },
          ]}
          heading=''
        />

        <Card>
          <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label={i18n('admin.newOrganization.general')} />
            {isAllowedToChildOrgs && isEdit && <Tab label={i18n('admin.newOrganization.child')} />}
            {isEdit && <Tab label={i18n('admin.newOrganization.users')} />}
          </Tabs>

          <Divider />

          <TabPanel value={currentTab} index={0}>
            <OrganizationNewEditForm 
              isEdit={isEdit}
              currentOrganization={currentOrganization} 
            />
          </TabPanel>

          {isAllowedToChildOrgs && isEdit && (
            <TabPanel value={currentTab} index={1}>
              <ChildList
                currentOrganization={currentOrganization} 
              />
            </TabPanel>
          )}

          <TabPanel value={currentTab} index={2}>
            <OrganizationUsers currentOrganization={currentOrganization}/>
          </TabPanel>
        </Card>
      </Container>
    </Page>
  );
}
